import React, {useContext, useEffect} from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import Title from 'src/components/AccountSettings/Title';
import Label from 'shared/components/label';
import AccountSettingsFormFooter from 'shared/account-settings/components/account-settings-form-footer';
import placeholders from 'src/static/placeholders.json';
import errorStrings from 'src/static/errors.json';
import {PathDesignContext} from 'src/components/path-design-context';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikTypeahead from 'src/components/formik-typeahead';
import {FormikEllucianTextInput} from 'src/components/ellucian/text-input';
import {FormikEllucianAutocomplete} from 'src/components/ellucian/autocomplete';
import {FormikEllucianDropdown} from 'src/components/ellucian/dropdown';
import {useAccountSettingsContext} from '../../../../components/AccountSettings/AccountSettingsContext';
import {CollegePortalAccountSettings} from '../college-portal-account-settings';
import {useDispatch} from 'react-redux';
import {receiveCollegePortalUser} from '../../../../redux/college-portal/actions/college-portal-user';
import {errorMessage} from 'shared/actions/notifications';

const DEPARTMENTS = [
  'Admissions - First Time Freshmen',
  'Admissions - Transfers Recruitment',
  'Financial Aid / Financial Services',
  'Scholarships',
  'Data & Operations',
  'Marketing or Communications',
  'Enrollment Management (Admissions & Financial Aid)',
  'University Relations',
  'Administrative',
];

const DEPARTMENTS_OPTIONS = DEPARTMENTS.map((department) => ({
  value: department,
  text: department,
}));

const ROLES = [
  'Director of Admissions',
  'Director of Transfer Admissions',
  'Associate Director of Admissions',
  'Associate Director of Transfer Admissions',
  'Admissions Counselor / Recruiter',
  'Transfer Admissions Counselor / Recruiter',
  'Director of Financial Aid',
  'Associate Director of Financial Aid',
  'Financial Aid Counselor',
  'Data Analyst / Operations Manager',
  'Director of Marketing & Communications',
  'Dean of Enrollment',
  'Vice President of Enrollment Management',
  'Provost',
];

const ProfileForm = () => {
  const dispatch = useDispatch();
  const {saveSettings, settings, setNavigationAnnouncement} =
    useAccountSettingsContext<CollegePortalAccountSettings>();
  const path = useContext(PathDesignContext);

  useEffect(() => {
    setNavigationAnnouncement('Edit your Profile');
  }, [setNavigationAnnouncement]);

  const validationSchema = Yup.object({
    firstName: Yup.string().required(errorStrings.validation.firstName.required),
    lastName: Yup.string().required(errorStrings.validation.lastName.required),
    title: Yup.string().nullable(),
    department: Yup.string().nullable(),
    role: Yup.string().nullable(),
  });

  return (
    <Formik
      initialValues={{
        _id: settings._id,
        firstName: settings.firstName,
        lastName: settings.lastName,
        title: settings.title,
        department: settings.department,
        role: (settings.role || '').replace('Other: ', ''),
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        let role = values.role;

        if (!ROLES.includes(role)) {
          role = `Other: ${role}`;
        }

        dispatch(receiveCollegePortalUser(values));
        return saveSettings({...values, role});
      }}
      validateOnMount
    >
      {({handleSubmit, validateForm, isSubmitting, values, setFieldValue}) => (
        <div className="account-settings-form">
          <Title>Edit Profile</Title>
          <Form autoComplete="off">
            <legend className="shared-required-legend"></legend>
            <section className="account-settings-form-section related">
              <div className="form-container">
                <div className="account-settings-form-name-with-title">
                  {path.enabled ? (
                    <>
                      <FormikEllucianTextInput
                        name="title"
                        label="Title"
                        placeholder={placeholders.title}
                        required
                        fullWidth
                      />

                      <FormikEllucianTextInput
                        name="firstName"
                        label="First Name"
                        placeholder={placeholders.firstName}
                        required
                        fullWidth
                      />

                      <FormikEllucianTextInput
                        name="lastName"
                        label="Last Name"
                        placeholder={placeholders.lastName}
                        required
                        fullWidth
                      />
                    </>
                  ) : (
                    <>
                      <Label required>
                        Title
                        <FormikTextInput
                          name="title"
                          showCheck
                          required
                          placeholder={placeholders.title}
                        />
                      </Label>

                      <Label required>
                        First Name
                        <FormikTextInput
                          name="firstName"
                          showCheck
                          required
                          placeholder={placeholders.firstName}
                        />
                      </Label>

                      <Label required>
                        Last Name
                        <FormikTextInput
                          name="lastName"
                          showCheck
                          required
                          placeholder={placeholders.lastName}
                        />
                      </Label>
                    </>
                  )}
                </div>
              </div>
            </section>
            <section className="college-portal-account-settings-role">
              {path.enabled ? (
                <FormikEllucianAutocomplete
                  name="role"
                  label="What is your current role?"
                  placeholder={placeholders.role}
                  options={ROLES}
                  fullWidth
                  freeSolo
                  cy="college-portal-account-settings-role"
                  className="mb-20"
                />
              ) : (
                <Label>
                  What is your current role?
                  <FormikTypeahead
                    id="college-portal-user-account-settings-role-typeahead"
                    name="role"
                    allowNew
                    options={ROLES}
                    onSelect={(value) => {
                      if (typeof value === 'string') {
                        setFieldValue('role', value);
                      } else {
                        setFieldValue('role', value.text);
                      }
                    }}
                    inputProps={{
                      'data-testid': 'college-portal-account-settings-role',
                    }}
                    placeholder={placeholders.role}
                  />
                </Label>
              )}
            </section>
            <section className="college-portal-account-settings-department">
              {path.enabled ? (
                <FormikEllucianDropdown
                  name="department"
                  label="What department do you work in?"
                  placeholder={placeholders.department}
                  options={DEPARTMENTS}
                  fullWidth
                />
              ) : (
                <Label>
                  What department do you work in?
                  <FormikDropdown
                    placeholder={placeholders.department}
                    name="department"
                    options={DEPARTMENTS_OPTIONS}
                  />
                </Label>
              )}
            </section>
            {/* Hidden submit button allows submission via footer */}
            <button type="submit" style={{display: 'none'}} aria-hidden="true" />
          </Form>
          <AccountSettingsFormFooter
            deleteAccountText="Are you sure you want to delete your account and remove all Connections to your current students and their progress?"
            includeDelete
            studentId={values._id}
            submitting={isSubmitting}
            disabled={isSubmitting}
            handleSubmit={async () => {
              const errors = await validateForm();
              if (Object.keys(errors).length > 0) {
                dispatch(errorMessage(`Form errors: ${Object.values(errors).join(', ')}`));
                return;
              }
              handleSubmit();
            }}
          />
        </div>
      )}
    </Formik>
  );
};

export default ProfileForm;
